import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';

// Single green hue, light -> dark, mapped to the 3 lifecycle buckets
export const STATUS_BUCKET_COLORS = {
  in_planning: '#BBF7D0', // light green
  scheduled: '#22C55E',   // medium green
  completed: '#166534',   // dark green
};

const STATUS_BUCKET_LABELS = {
  in_planning: 'In Planning',
  scheduled: 'Scheduled',
  completed: 'Completed',
};

/**
 * Stacked horizontal bar chart: one bar per partner, segmented by status
 * (In Planning / Scheduled / Completed) in light-to-dark green shades.
 * Cancelled projects are intentionally excluded from the segments.
 */
export const TrainingsByPartnerChart = ({ partnerBreakdown = [], onBarClick, testId = 'chart-partners' }) => {
  const data = partnerBreakdown
    .slice(0, 8)
    .map(p => ({
      name: p.partner_name,
      partner_id: p.partner_id,
      in_planning: p.in_planning || 0,
      scheduled: p.scheduled || 0,
      completed: p.completed || 0,
    }));

  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="font-heading text-base">Trainings by Partner</CardTitle>
        <CardDescription className="text-xs">Click a bar to view partner details</CardDescription>
      </CardHeader>
      <CardContent>
        {data.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={Math.max(180, data.length * 34)}>
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} tickLine={false} />
                <RechartsTooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-white border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                        <p className="font-semibold mb-1">{label}</p>
                        {payload.map((p) => (
                          <p key={p.dataKey} className="text-xs flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full inline-block" style={{ background: STATUS_BUCKET_COLORS[p.dataKey] }} />
                            {STATUS_BUCKET_LABELS[p.dataKey]}: <span className="font-medium">{p.value}</span>
                          </p>
                        ))}
                      </div>
                    );
                  }}
                />
                <Bar dataKey="in_planning" stackId="s" name="In Planning" fill={STATUS_BUCKET_COLORS.in_planning} className="cursor-pointer" radius={[0, 0, 0, 0]}
                  onClick={(data) => { if (onBarClick) onBarClick(data?.payload || data); }} />
                <Bar dataKey="scheduled" stackId="s" name="Scheduled" fill={STATUS_BUCKET_COLORS.scheduled} className="cursor-pointer" radius={[0, 0, 0, 0]}
                  onClick={(data) => { if (onBarClick) onBarClick(data?.payload || data); }} />
                <Bar dataKey="completed" stackId="s" name="Completed" fill={STATUS_BUCKET_COLORS.completed} className="cursor-pointer" radius={[0, 4, 4, 0]}
                  onClick={(data) => { if (onBarClick) onBarClick(data?.payload || data); }} />
              </BarChart>
            </ResponsiveContainer>
            {/* Legend */}
            <div className="flex items-center gap-4 mt-2 pt-2 border-t text-xs text-muted-foreground">
              {Object.entries(STATUS_BUCKET_LABELS).map(([key, label]) => (
                <span key={key} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: STATUS_BUCKET_COLORS[key] }} />
                  {label}
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="text-center text-muted-foreground py-4 text-sm">No data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default TrainingsByPartnerChart;
